import { z } from "zod";
import { safeLocalStorage } from "@/lib/storage/safe-storage";
import type { QuoteFormState } from "./listing-quote-request-state";

const KEY_PREFIX = "listing_guest_quote_pending_v1_";
const PENDING_MAX_AGE_MS = 60 * 60 * 1000;

const pendingGuestQuoteSchema = z.object({
  email: z.string().min(1),
  savedAt: z.number(),
  form: z.object({
    contact: z.union([
      z.object({
        name: z.string(),
        email: z.string(),
        phone: z.string(),
      }),
      z
        .object({
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
          phone: z.string(),
        })
        .transform(({ firstName, lastName, email, phone }) => ({
          name: [firstName, lastName].filter(Boolean).join(" "),
          email,
          phone,
        })),
    ]),
    quantity: z.number(),
    quantityDisplay: z.string(),
    fulfillmentType: z.enum(["delivery", "pickup"]),
    preferredDate: z.string(),
    notes: z.string(),
    deliveryAddress: z.object({
      line1: z.string(),
      line2: z.string().optional(),
      city: z.string(),
      state: z.string(),
      postalCode: z.string(),
      country: z.string(),
    }),
  }),
});

type PendingForm = z.infer<typeof pendingGuestQuoteSchema>["form"];

export interface PendingGuestQuote {
  email: string;
  form: PendingForm;
}

function pendingKey(listingId: string): string {
  return `${KEY_PREFIX}${listingId}`;
}

function parseStoredJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function readPendingGuestQuote(
  listingId: string,
  now: number = Date.now(),
): PendingGuestQuote | null {
  const raw = safeLocalStorage().getItem(pendingKey(listingId));
  if (!raw) return null;
  const parsed = pendingGuestQuoteSchema.safeParse(parseStoredJson(raw));
  if (parsed.success && now - parsed.data.savedAt <= PENDING_MAX_AGE_MS) {
    return { email: parsed.data.email, form: parsed.data.form };
  }
  clearPendingGuestQuote(listingId);
  return null;
}

export function writePendingGuestQuote(
  listingId: string,
  email: string,
  state: QuoteFormState,
  now: number = Date.now(),
): void {
  const form: PendingForm = {
    contact: state.contact,
    quantity: state.quantity,
    quantityDisplay: state.quantityDisplay,
    fulfillmentType: state.fulfillmentType,
    preferredDate: state.preferredDate,
    notes: state.notes,
    deliveryAddress: state.deliveryAddress,
  };
  safeLocalStorage().setItem(
    pendingKey(listingId),
    JSON.stringify({ email, savedAt: now, form }),
  );
}

export function clearPendingGuestQuote(listingId: string): void {
  safeLocalStorage().removeItem(pendingKey(listingId));
}
